import type { ReactiveControllerHost, ReactiveController } from 'lit';
import createSelectionStrategy from '@loadsmart/miranda-toolset/dist/selection';
import type {
	AcceptedType,
	SelectionState,
	SelectionStrategy,
	SelectionType,
} from '@loadsmart/miranda-toolset/dist/selection';
import toArray from '@loadsmart/miranda-toolset/dist/to-array';

type SelectionHost = ReactiveControllerHost &
	HTMLElement & { selection: SelectionState; type: SelectionType };

type SelectionEvent = {
	type: 'select' | 'unselect' | 'toggle';
	value: AcceptedType | AcceptedType[];
};

/**
 * Manage selection through the SelectionStrategy.
 * `host` is expected to have a `selection` and a `type` (`SelectionType`) properties/state.
 */
class SelectionController implements ReactiveController {
	disabled = false;

	event = 'm-select';

	host: SelectionHost;

	strategy: SelectionStrategy;

	type: SelectionType;

	/**
	 *
	 * @param {SelectionHost} host
	 * @param {SelectionType} type - Type of selection to be managed.
	 * @param {string} event - Name of the event that should be listened to.
	 */
	constructor(host: SelectionHost, type: SelectionType, event: string) {
		this.host = host;
		this.type = type;
		this.event = event;

		this.strategy = createSelectionStrategy({ type });

		this.host.addController(this);
	}

	hostConnected() {
		this.subscribe();
	}

	hostDisconnected() {
		this.unsubscribe();
	}

	hostUpdate() {
		/**
		 * [!] This flow covers the case where the `host` changes its selection strategy (`type`).
		 * The `hostUpdate` lifecycle happens before the `host`'s `update` [Source](https://lit.dev/docs/composition/controllers/#lifecycle).
		 */
		if (this.host.type != null && this.host.type !== this.type) {
			this.unsubscribe();
			this.host.removeController(this);
		}
	}

	/**
	 * using arrow function so we keep the lexical context this is necessary
	 * because the event itself will happen in the context of the host element.
	 */
	#handleSelectionEvent = (e: Event) => {
		if (this.disabled) {
			return;
		}

		if (this.type == null) {
			console.warn(
				'[SelectionController] Did you forget to set the selection type?',
			);
			return;
		}

		const {
			detail: { type, value },
		} = e as CustomEvent<SelectionEvent>;

		switch (type) {
			case 'select':
				this.select(toArray(value));
				break;
			case 'unselect':
				this.unselect(toArray(value));
				break;
			case 'toggle':
				this.toggle(toArray(value));
				break;
			default:
		}
	};

	#publish() {
		const options = {
			detail: {
				type: this.type,
				value: this.strategy.value(this.host.selection),
			},
			bubbles: true,
			composed: false,
		};

		this.host.dispatchEvent(new CustomEvent('m-change', options));
	}

	init = (values: AcceptedType[]) => {
		this.host.selection = this.strategy.init(values);
	};

	select = (values: AcceptedType[]) => {
		this.host.selection = this.strategy.select(values, this.host.selection);

		this.#publish();
	};

	unselect = (values: AcceptedType[]) => {
		this.host.selection = this.strategy.unselect(values, this.host.selection);

		this.#publish();
	};

	toggle = (values: AcceptedType[]) => {
		this.host.selection = this.strategy.toggle(values, this.host.selection);

		this.#publish();
	};

	subscribe = () => {
		this.host.addEventListener(this.event, this.#handleSelectionEvent);
	};

	unsubscribe = () => {
		this.host.removeEventListener(this.event, this.#handleSelectionEvent);
	};
}

export default SelectionController;
